import type {
  AgendaCategoryColor,
  AttendanceStatus,
  CostCenterKind,
  Currency,
  ErrorGroupStatus,
  NonBusinessDayPolicy,
  RoutineKind,
  TicketSource,
  TicketStatus,
  TriageStatus,
} from './enums.js'

/**
 * Domínio das capacidades C1, C4–C10.
 * Ver docs/SDD/05, 08, 09, 10, 11, 12, 13, 19.
 */

// --- C1 Pessoas -------------------------------------------------------------

export interface Position {
  readonly id: string
  readonly title: string
  readonly level?: string
}

export interface PersonCost {
  readonly memberId: string
  readonly effectiveFrom: string
  readonly monthlyTotal: number
  readonly salary?: number
  readonly charges?: number
  readonly benefits?: number
}

/**
 * Custo plenamente carregado: salário + encargos + benefícios.
 *
 * É este número — não o salário — que entra na estimativa por projeto. Usar o
 * salário puro subestima o custo do departamento em torno de um terço.
 */
export function fullyLoadedCost(input: {
  salary: number
  charges?: number
  benefits?: number
}): number {
  const total = input.salary + (input.charges ?? 0) + (input.benefits ?? 0)
  return Math.round(total * 100) / 100
}

/**
 * Encargos estimados sobre o salário. Referência de partida para o formulário
 * — a pessoa que cadastra ajusta; o Hub nunca decide encargo sozinho.
 */
export const DEFAULT_CHARGES_RATE = 0.7

export function estimateCharges(salary: number, rate = DEFAULT_CHARGES_RATE): number {
  return Math.round(salary * rate * 100) / 100
}

/**
 * Vigência aplicável numa data: a mais recente que já começou.
 *
 * Alterar salário cria vigência nova; o histórico não é sobrescrito, senão o
 * custo de meses fechados mudaria retroativamente.
 */
export function costOn(
  costs: readonly PersonCost[],
  memberId: string,
  reference: string,
): PersonCost | null {
  const applicable = costs
    .filter((c) => c.memberId === memberId && c.effectiveFrom <= reference)
    .sort((a, b) => a.effectiveFrom.localeCompare(b.effectiveFrom))
  return applicable.at(-1) ?? null
}

/** Q13/Q49 — custo por projeto é estimativa por alocação, sem timesheet. */
export interface Allocation {
  readonly id: string
  readonly memberId: string
  readonly projectId: string
  readonly month: string
  readonly percent: number
}

// --- C4 Chamados ------------------------------------------------------------

export interface Ticket {
  readonly id: string
  readonly source: TicketSource
  /** Só o espelho do Portal tem; o chamado do Hub nasce sem. */
  readonly portalId?: string
  /** `CH-n` no Hub; o número do Portal no espelho. */
  readonly number: string
  readonly title: string
  readonly description: string
  readonly requesterName: string
  readonly requesterSector: string
  readonly requesterContact: string
  readonly category: string
  readonly priority: string
  readonly status: TicketStatus
  readonly triageStatus: TriageStatus
  readonly openedAt: string
  /** Quem registrou no Hub (o espelho não tem). */
  readonly createdBy?: string
  readonly triagedBy?: string
  /** Última mudança da triagem — a hora em que foi triado ou recusado. */
  readonly triagedAt?: string
  readonly slaDueAt?: string
  /** N:N com atividade (Q04). Encerra quando todos os obrigatórios concluírem. */
  readonly activityIds: readonly string[]
  /** Devolutiva ao solicitante, ou o motivo da recusa. */
  readonly resolution?: string
  readonly answeredAt?: string
  /** Catálogo (ADR-0018); ausente em "novo sistema" / "não sei" e no espelho antigo. */
  readonly systemId?: string
  readonly systemChoice: 'sistema' | 'novo_sistema' | 'nao_sei'
  /** `5562999998888`. O bot manda o andamento para cá (ADR-0020). */
  readonly requesterWhatsapp?: string
  readonly requesterExtension?: string
  /** O atendente: um por chamado. */
  readonly attendantId?: string
  /** Código do link de acompanhamento (`/c/K7P2QX9M`). */
  readonly publicCode: string
  readonly resolvedAt?: string
  /** Até quando quem abriu pode reabrir (2 dias úteis depois de resolvido). */
  readonly reopenUntil?: string
  /** Último andamento: mensagem, status ou atividade movida. */
  readonly lastProgressAt: string
}

/** Mensagem do chamado: a linha do tempo pública e o chat interno do time. */
export interface TicketMessage {
  readonly id: string
  readonly ticketId: string
  readonly authorKind: 'atendente' | 'solicitante' | 'sistema' | 'time'
  readonly authorMemberId?: string
  /** `publico` o solicitante vê (e vai no WhatsApp); `interno` é só do time. */
  readonly visibility: 'publico' | 'interno'
  readonly body: string
  readonly statusFrom?: TicketStatus
  readonly statusTo?: TicketStatus
  readonly createdAt: string
}

// --- C5 Rotinas -------------------------------------------------------------

/**
 * Categoria da agenda — taxonomia única do departamento.
 *
 * A mesma categoria classifica rotina, evento avulso, ausência e prazo: é o
 * que permite perguntar "quanto do mês foi cerimônia e quanto foi entrega?"
 * sem inventar um vocabulário por tela. Cadastrável de propósito — o ciclo do
 * departamento cresce, e categoria fixa em código obrigaria deploy para cada
 * assunto novo (mesma razão do catálogo de rotinas, SDD 09 §3).
 */
export interface AgendaCategory {
  readonly id: string
  readonly name: string
  /** Chave do token de cor, não a cor. Ver AGENDA_CATEGORY_COLORS. */
  readonly color: AgendaCategoryColor
  readonly description?: string
  /** Ordem de exibição nas listas e filtros; empate resolve pelo nome. */
  readonly position: number
  readonly isActive: boolean
}

export interface Routine {
  readonly id: string
  readonly name: string
  readonly kind: RoutineKind
  /** Categoria da agenda; ausente = rotina ainda não classificada. */
  readonly categoryId?: string
  readonly weekday?: number
  /** Cadência ordinal: com `weekday`, "a N-ésima terça do mês". -1 = a última. */
  readonly weekOfMonth?: number
  readonly monthDay?: number
  readonly time: string
  readonly durationMinutes: number
  readonly participantIds: readonly string[]
  readonly ownerId: string
  readonly nonBusinessDayPolicy: NonBusinessDayPolicy
  readonly isActive: boolean
}

export interface Occurrence {
  readonly id: string
  readonly routineId: string
  /** Onde a ocorrência realmente acontece — muda ao remarcar. */
  readonly date: string
  /**
   * A vaga na série de recorrência (migration 020).
   *
   * É o que permite adiar UMA ocorrência sem mexer na regra: o calendário gera
   * a série, encontra o override desta vaga e mostra `date` no lugar dela.
   */
  readonly originalDate: string
  readonly status: 'agendada' | 'realizada' | 'adiada' | 'cancelada' | 'nao_realizada'
  readonly agenda?: string
  readonly minutes?: string
  readonly attendance: Readonly<Record<string, AttendanceStatus>>
}

export type EventVisibility = 'publico' | 'ocupado' | 'privado'

/**
 * Compromisso pontual da agenda (SDD 09 §2).
 *
 * Vem da view `calendar_event_visible`, que já aplicou a visibilidade: em
 * `ocupado`, o título chega como "Ocupado" e descrição, local e categoria
 * chegam vazios. `canSeeDetail` diz qual dos dois casos é — a tela não
 * adivinha pela visibilidade, porque dono e participante veem tudo mesmo em
 * compromisso `ocupado`.
 */
export interface CalendarEvent {
  readonly id: string
  readonly ownerId: string
  readonly title: string
  readonly description?: string
  readonly location?: string
  readonly categoryId?: string
  readonly date: string
  /** `HH:MM`. Ausente = dia inteiro (férias, folga, feriado). */
  readonly time?: string
  readonly durationMinutes: number
  readonly visibility: EventVisibility
  readonly canSeeDetail: boolean
  readonly participantIds: readonly string[]
}

// --- C6 DevOps --------------------------------------------------------------

export interface Repository {
  readonly id: string
  readonly name: string
  readonly hasProduction: boolean
  readonly projectIds: readonly string[]
}

/** Um check do último commit do PR, como o worker traduziu do GitHub. */
export interface PrCheck {
  readonly name: string
  /** `ignorado` = pulado ou neutro: não conta a favor nem contra. */
  readonly state: 'sucesso' | 'falha' | 'pendente' | 'ignorado'
  readonly url?: string
}

export interface PullRequest {
  readonly id: string
  readonly repositoryId: string
  readonly number: number
  readonly title: string
  readonly authorLogin: string
  readonly authorMemberId?: string
  /** Foto do autor no GitHub. A tela prefere a do Hub quando o autor é membro. */
  readonly authorAvatarUrl?: string
  readonly state: 'aberto' | 'merged' | 'fechado'
  readonly isDraft: boolean
  /**
   * `sem_checks` = o repositório não tem CI configurado. Diferente de
   * `pendente`, que é CI rodando. Tratar os dois como iguais fazia o card
   * "PRs aguardando review" marcar zero para sempre.
   */
  readonly checksStatus: 'sucesso' | 'falha' | 'pendente' | 'sem_checks'
  /** Checks um a um. Vazio em PR coletado antes de 2026-10-08. */
  readonly checks?: readonly PrCheck[]
  /** Revisores DISTINTOS cuja última review é APPROVED. */
  readonly approvals: number
  readonly changesRequested: number
  readonly openedAt: string
  readonly mergedAt?: string
  readonly closedAt?: string
  readonly firstApprovedAt?: string
  readonly activityKey?: string
  readonly additions: number
  readonly deletions: number
  /** Branch de destino (`main`, `production`). Ausente em linha coletada antes de 27/09. */
  readonly baseRef?: string
  /** Branch de origem. */
  readonly headRef?: string
  readonly mergedByLogin?: string
  readonly url?: string
}

/**
 * Commit da branch padrão de um repositório.
 *
 * `Commit` e não `RepoCommit` do lado do TypeScript: o prefixo no banco existe
 * só porque `commit` é palavra reservada em SQL.
 */
export interface Commit {
  readonly id: string
  readonly repositoryId: string
  readonly sha: string
  readonly branch: string
  readonly authorLogin?: string
  readonly authorMemberId?: string
  readonly message: string
  readonly committedAt: string
  /** `null` = não coletado. Nunca 0 por omissão: 0 seria commit vazio. */
  readonly additions: number | null
  readonly deletions: number | null
  /** Merge não conta como trabalho de quem apertou o botão. */
  readonly isMerge: boolean
}

export interface Deployment {
  readonly id: string
  readonly repositoryId: string
  readonly environment: 'production' | 'staging'
  readonly version: string
  readonly status: 'sucesso' | 'falha' | 'rollback'
  readonly finishedAt: string
  readonly durationSeconds: number
  readonly workflowName?: string
  /** Quem disparou o run (`triggering_actor`). */
  readonly actorLogin?: string
  readonly headSha?: string
  readonly runUrl?: string
  /** Tentativa do run; 1 = passou de primeira. */
  readonly runAttempt?: number
  readonly event?: string
}

// --- C7 Monitoramento -------------------------------------------------------

export interface Host {
  readonly id: string
  readonly name: string
  readonly address: string
  readonly role: string
  /** `null` = o Prometheus não trouxe a métrica. Nunca 0 por omissão: 0% é afirmação. */
  readonly cpuPercent: number | null
  readonly memoryPercent: number | null
  readonly diskPercent: number | null
  readonly uptimeDays: number | null
  readonly lastSeenSecondsAgo: number
}

export interface ServiceStatus {
  readonly id: string
  readonly name: string
  readonly hostId: string
  readonly state: 'ativo' | 'parado' | 'degradado'
  readonly uptimeDays: number
}

export interface Frontier {
  readonly id: string
  readonly name: string
  readonly state: 'conectada' | 'degradada' | 'desligada'
  readonly lastEventLabel: string
}

export interface ErrorGroup {
  readonly id: string
  readonly serviceId: string
  readonly title: string
  readonly occurrences: number
  readonly affectedUsers: number
  readonly firstSeen: string
  readonly lastSeen: string
  readonly trend: 'subindo' | 'estavel' | 'caindo'
  readonly status: ErrorGroupStatus
  readonly activityId?: string
}

export interface MonitorAlert {
  readonly id: string
  readonly severity: 'critico' | 'atencao' | 'info'
  readonly source: 'host' | 'servico' | 'fronteira' | 'erro'
  readonly sourceId: string
  readonly title: string
  readonly detail: string
  readonly since: string
  readonly acknowledged: boolean
}

// --- C9 Gamificação ---------------------------------------------------------

export interface GoldEntry {
  readonly id: string
  readonly memberId: string
  readonly points: number
  readonly reason: 'atividade_concluida' | 'ajuste_manual' | 'bonus' | 'estorno'
  readonly activityKey?: string
  readonly description: string
  readonly month: string
  readonly createdAt: string
}

/** Q46 — base max(1, dificuldade), +20% prazo, +10% revisão, teto 15. */
export function goldForActivity(input: {
  difficulty: number | null
  onTime: boolean
  reviewed: boolean
}): number {
  const base = Math.max(1, input.difficulty ?? 1)
  let total = base
  if (input.onTime) total += base * 0.2
  if (input.reviewed) total += base * 0.1
  return Math.min(15, Math.round(total))
}

// --- C10 Financeiro ---------------------------------------------------------

export interface BudgetMonth {
  readonly id: string
  readonly month: string
  readonly budgetBrl: number
  readonly isManual: boolean
}

export interface ExpenseSplit {
  readonly kind: CostCenterKind
  readonly targetId: string
  readonly percent: number
}

export interface Expense {
  readonly id: string
  readonly paidAt: string
  readonly vendor: string
  readonly category: string
  readonly currency: Currency
  readonly originalAmount: number
  readonly fxRate: number
  readonly fxRateDate: string
  readonly fxSource: string
  readonly amountBrl: number
  readonly description?: string
  readonly receiptName: string
  readonly splits: readonly ExpenseSplit[]
  readonly recurring: boolean
  readonly renewalDate?: string
  readonly createdBy: string
  /** SIS-63: parcela de um lançamento parcelado. Ausente = lançamento avulso. */
  readonly installment?: ExpenseInstallment
}

export interface ExpenseInstallment {
  readonly group: string
  /** 1..count. O comprovante fica na parcela 1. */
  readonly number: number
  readonly count: number
}

export const MIN_INSTALLMENTS = 2
export const MAX_INSTALLMENTS = 60

/**
 * SIS-63: divide o TOTAL em `count` parcelas, em centavos, com a sobra na 1ª.
 * Espelha `sistemas.create_expense_installments` — a prévia da tela tem de
 * bater com o que o banco grava. `null` quando não dá um centavo por parcela.
 */
export function splitInstallments(total: number, count: number): number[] | null {
  const cents = Math.round(total * 100)
  if (!Number.isInteger(count) || count < MIN_INSTALLMENTS || count > MAX_INSTALLMENTS) return null
  if (!(cents >= count)) return null
  const base = Math.floor(cents / count)
  return Array.from({ length: count }, (_, i) => (i === 0 ? cents - base * (count - 1) : base) / 100)
}

/**
 * De quem é o comprovante deste lançamento: a parcela 1 do grupo, para as
 * outras parcelas; o próprio lançamento, para o resto (e para a parcela 1).
 * Se a parcela 1 não estiver carregada, cai no próprio id — e a tela acusa
 * "sem comprovante", que é a verdade do que ela consegue ver.
 */
export function receiptHolderId(expense: Expense, expenses: readonly Expense[]): string {
  const inst = expense.installment
  if (!inst || inst.number === 1) return expense.id
  return expenses.find((e) => e.installment?.group === inst.group && e.installment.number === 1)?.id ?? expense.id
}

/** "2/3" para a parcela 2 de 3; `null` para lançamento avulso. */
export function installmentLabel(expense: Pick<Expense, 'installment'>): string | null {
  return expense.installment ? `${expense.installment.number}/${expense.installment.count}` : null
}

/** Invariante 6/7 do SYS: BRL usa taxa 1; USD registra taxa, data e fonte. */
export function normalizeToBrl(input: {
  currency: Currency
  originalAmount: number
  fxRate: number
}): number {
  if (input.currency === 'BRL') return Math.round(input.originalAmount * 100) / 100
  return Math.round(input.originalAmount * input.fxRate * 100) / 100
}

export function savingPercent(budget: number, realized: number): number | null {
  if (budget <= 0) return null
  return Math.round(((budget - realized) / budget) * 1000) / 10
}

/** Q55 — avisos em 80, 90 e 100% do teto. */
export function budgetAlertLevel(
  budget: number,
  realized: number,
): 'ok' | 'atencao' | 'critico' | 'estourado' {
  if (budget <= 0) return 'ok'
  const usage = (realized / budget) * 100
  if (usage > 100) return 'estourado'
  if (usage >= 90) return 'critico'
  if (usage >= 80) return 'atencao'
  return 'ok'
}

/** Q54 — rateio precisa somar exatamente 100%, ou nenhum rateio. */
export function isValidSplit(splits: readonly ExpenseSplit[]): boolean {
  if (splits.length === 0) return true
  const total = splits.reduce((sum, s) => sum + s.percent, 0)
  return Math.abs(total - 100) < 0.001
}

export function formatBrl(value: number): string {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

export function monthLabel(month: string): string {
  const nomes = [
    'jan', 'fev', 'mar', 'abr', 'mai', 'jun',
    'jul', 'ago', 'set', 'out', 'nov', 'dez',
  ]
  const [year, m] = month.split('-')
  const index = Number(m) - 1
  return `${nomes[index] ?? '?'}/${year?.slice(2) ?? '??'}`
}

// --- C1 Tempo de casa -------------------------------------------------------

export interface Tenure {
  readonly years: number
  readonly months: number
  /** "3 anos e 2 meses", "1 mês", "menos de 1 mês". */
  readonly label: string
}

/**
 * Tempo de casa a partir da admissão, em datas de calendário (`yyyy-MM-dd`).
 *
 * Contagem por componente, não por divisão de milissegundos: 365 dias não são
 * um ano em anos bissextos, e "2 anos e 11 meses" precisa sair exato no dia em
 * que a pessoa completa o mês, não um dia depois.
 *
 * Admissão no futuro devolve `null` — data digitada errada não vira "0 meses"
 * silencioso na tela.
 */
export function tenure(startedOn: string, reference: string): Tenure | null {
  const start = splitIsoDate(startedOn)
  const ref = splitIsoDate(reference)
  if (!start || !ref) return null
  if (startedOn > reference) return null

  let months = (ref.year - start.year) * 12 + (ref.month - start.month)
  // O mês só fecha no dia do aniversário: admitido dia 20, no dia 19 do mês
  // seguinte ainda não completou um mês.
  if (ref.day < start.day) months -= 1
  if (months < 0) months = 0

  const years = Math.floor(months / 12)
  const restMonths = months % 12

  return { years, months: restMonths, label: tenureLabel(years, restMonths) }
}

function tenureLabel(years: number, months: number): string {
  const parts: string[] = []
  if (years > 0) parts.push(years === 1 ? '1 ano' : `${years} anos`)
  if (months > 0) parts.push(months === 1 ? '1 mês' : `${months} meses`)
  if (parts.length === 0) return 'menos de 1 mês'
  return parts.join(' e ')
}

function splitIsoDate(iso: string): { year: number; month: number; day: number } | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso)
  if (!match) return null
  const [, y, m, d] = match
  return { year: Number(y), month: Number(m), day: Number(d) }
}
